import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.js';
import { AppCrashed, ErrorBoundary, reportClientError } from './components/ErrorBoundary.js';
import './styles.css';
import { APP_BUILD, NATIVE } from './lib/platform.js';
import { applyLook, keptLook } from './lib/look.js';
import { SplashScreen } from '@capacitor/splash-screen';
import { startFpsProbe } from './lib/fpsProbe.js';

// The customer app is light only (Saif, 22 Oct 2026): there is no dark switch and the phone's dark setting is ignored.
document.documentElement.setAttribute('data-mode', 'light');
// The look last fetched from the admin's settings, before the first paint, so the app never flashes the old colours.
applyLook(keptLook());
// The opening (components/Intro.tsx): the page waits under the mark until the glide lets it in, from the first paint.
document.documentElement.setAttribute('data-intro', 'hold');
if (NATIVE) {
  document.documentElement.setAttribute('data-native', '1');
  // index.html has painted the launch screen's twin (#boot): once that frame is on screen the native launch screen goes,
  // with no fade, and the glide intro takes over from the same picture.
  requestAnimationFrame(() => requestAnimationFrame(() => SplashScreen.hide({ fadeOutDuration: 0 }).catch(() => undefined)));
}
document.documentElement.setAttribute('data-view', window.matchMedia('(min-width: 900px)').matches ? 'laptop' : 'phone');

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary where="app" fallback={() => <AppCrashed />}>
      <App />
    </ErrorBoundary>
  </StrictMode>,
);
// A promise that failed with nobody listening: counted, so it can be found (the boundaries catch render errors only).
window.addEventListener('unhandledrejection', (e) => reportClientError('unhandled', e.reason));

// Installable on the home screen: a small service worker caches the app shell (never the API). Not in the app: its files are on the phone.
if (!APP_BUILD && 'serviceWorker' in navigator && location.protocol === 'https:') {
  window.addEventListener('load', () => navigator.serviceWorker.register('/sw.js').catch(() => undefined));
}
// Local simulator builds only (VITE_FPS_PROBE=1): a frame-time probe for the card animations.
startFpsProbe();
